import useUserEditVersion from '@/features/config/hooks/useUserEditVersion'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type MutableRefObject,
} from 'react'
import { usePermission } from '@/hooks/permissions'
import useTranslation from '@/hooks/useTranslation'
import soilDataConfigApi from '@/api/config/soilDataConfigApi'
import { CONFIG_NAME_MAX_LENGTH } from '@/constant/common'
import toast from 'react-hot-toast'
import { getApiErrorMessage, isRetryableApiError } from '@/helpers/notify'
import {
  mergeKnownRows,
  retireCreatedDrafts,
  retireSavedRecords,
  splitEditsForSave,
  withoutKey,
} from '@/utils/stagedSave'
import { Box } from '@mui/material'
import { useSelector } from 'react-redux'
import { selectTheme } from '@/store/selectors/ThemeSelector'
import { mode_type } from '@/theme/types/constants'
import DeleteIconButton from '@/features/config/components/common/DeleteIconButton'
import DataGrid, {
  gridCellClasses,
  gridRowClasses,
} from '@/components/grid/DataGrid'
import useGuidedAutosave from '@/features/config/hooks/useGuidedAutosave'
import useSerialisedSave from '@/features/config/hooks/useSerialisedSave'
import { useIsGuidedSetup } from '@/features/config/contexts/guided-edit-context'
import type { GridApi, GridCellCtx, GridColumn } from '@/components/grid/types'
import {
  commitOrHoldOpenEditor,
  type GuidedSaveOptions,
} from '@/components/grid/commitOpenEditor'

// Writes must be serialised: the API returns 500 under concurrent writes.
const SAVE_RETRY_LIMIT = 3
const SAVE_RETRY_DELAY_MS = 300
const SAVE_REQUEST_GAP_MS = 120

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

/** The permanent "+ Add Soil" affordance. It holds no data and is never written. */
const ADD_ROW_ID = '__CREATE_ROW__'

/**
 * Draft ids come from a counter seeded here and decremented per draft, so any
 * number of drafts can be staged at once without colliding. Negative throughout:
 * the rest of the grids already read a negative id as "not on the server yet".
 */
const DRAFT_ID_START = -1_000_000
// Row ids arrive from the API as numbers even where the row type says
// string, so coerce before testing the draft marker.
const isDraftId = (id: string | number) => String(id).startsWith('-')

export interface SoilRow {
  id: string
  interfaceId?: string
  description: string
  holdingCapacity: number
  wiltingPoint: number
  intakeRate: number
  maxSurfaceAccumulation: number
}

// The update endpoint replaces the row, so the pending edit has to carry the
// soil's numeric properties as well as the new name.
interface PendingEdit {
  description: string
  holdingCapacity?: number
  wiltingPoint?: number
  intakeRate?: number
  maxSurfaceAccumulation?: number
}

/** A create staged in the grid; nothing is POSTed until the toolbar Save runs. */
interface DraftRow {
  id: string
  description: string
}

export interface SoilGridActions {
  save: () => Promise<{ failed: number; total: number }>
  cancel: () => void
  refresh: () => void
  hasPendingEdits: () => boolean
  /** Drops a row's staged edit once the row has been deleted. */
  forgetRow: (id: string) => void
  /** Stages a row from outside the grid — the quick-add chips. */
  addDraft: (values?: { description?: string }) => void
}

interface SoilTableProps {
  interfaceId: number
  keyword?: string
  bulkEditMode?: boolean
  onDeleteItem?: (id: string, interfaceId: string, name: string) => void
  onRowCountChange?: (count: number) => void
  /** The names staged but not yet written, so a chip can show as added. */
  onStagedNamesChange?: (names: string[]) => void
  actionsRef: MutableRefObject<SoilGridActions | null>
}

const SoilTable: FC<SoilTableProps> = ({
  interfaceId,
  keyword = '',
  bulkEditMode = false,
  onDeleteItem,
  onRowCountChange,
  onStagedNamesChange,
  actionsRef,
}) => {
  const { isViewOnly } = usePermission()
  // SCRUM-1032: inside the wizard the grid writes itself once the user pauses,
  // the same as the decoder and pipe lists. Not on /config, where Save is the
  // contract.
  const isGuidedSetup = useIsGuidedSetup()
  const { t } = useTranslation()
  const isDark = useSelector(selectTheme) === mode_type.dark
  const gridApiRef = useRef<GridApi<SoilRow> | null>(null)

  // Pending edits live here, NOT in the grid. A refetch replaces the base rows
  // at any time, which would throw away unsaved changes held on the rows.
  const [pendingEdits, setPendingEdits] = useState<Map<string, PendingEdit>>(
    new Map(),
  )
  const [userEditVersion, noteUserEdit] = useUserEditVersion()
  // The ref is the source of truth and is written the moment an edit is
  // staged, not on render: a save that starts with an editor open reads it
  // straight after `commitOpenEditor`, before React has rendered the state the
  // editor's commit set — reading a render-assigned ref missed that edit.
  const pendingEditsRef = useRef(pendingEdits)
  const updatePendingEdits = useCallback(
    (update: (prev: Map<string, PendingEdit>) => Map<string, PendingEdit>) => {
      const next = update(pendingEditsRef.current)
      if (next === pendingEditsRef.current) return
      pendingEditsRef.current = next
      setPendingEdits(next)
    },
    [],
  )

  // Staged creates, in the order they were added. They live outside `rows` so a
  // refetch — which replaces the server rows — cannot drop them; `rowData`
  // splices them back in for display.
  const [drafts, setDrafts] = useState<DraftRow[]>([])
  const draftsRef = useRef(drafts)
  draftsRef.current = drafts
  const draftIdRef = useRef(DRAFT_ID_START)

  // Drafts a save refused to write, so the offending cell can be marked.
  const [invalidRowIds, setInvalidRowIds] = useState<Set<string>>(new Set())

  // All soils are loaded at once (a small config list) so the "Add Soil" row can
  // be appended as the real last row rather than pinned to the viewport bottom.
  const [rows, setRows] = useState<SoilRow[]>([])
  // Every row fetched, kept across a search so an edit on a row the search
  // hides is still sent (see splitEditsForSave). `listCompleteRef` says the
  // last fetch had no search, so a row missing from it has been deleted.
  const knownRowsRef = useRef<Map<string, SoilRow>>(new Map())
  const listCompleteRef = useRef(false)
  // Only the FIRST fetch draws skeleton rows. A refetch after a save or a
  // keyword change keeps the rows that are already on screen, so the list
  // never blinks out from under the user.
  const [loadedOnce, setLoadedOnce] = useState(false)

  const fetchRows = useCallback(async () => {
    try {
      const res: any = await soilDataConfigApi.getAll({
        interfaceId,
        PageIndex: 1,
        PageSize: 0,
        ...(keyword ? { Keyword: keyword } : undefined),
      } as any)
      const list: SoilRow[] = res?.data?.data ?? []
      knownRowsRef.current = mergeKnownRows(knownRowsRef.current, list, !keyword)
      listCompleteRef.current = !keyword
      setRows(list)
      onRowCountChange?.(res?.data?.totalCount ?? list.length)
    } catch (error: any) {
      toast.error(error?.message || t('somethingWentWrong'))
    } finally {
      setLoadedOnce(true)
    }
  }, [interfaceId, keyword, onRowCountChange, t])

  useEffect(() => {
    fetchRows()
  }, [fetchRows])

  const refresh = useCallback(() => {
    fetchRows()
  }, [fetchRows])

  // The chip stays put once a draft has been added, so several soils can be
  // staged one after another.
  const addRow = useMemo<SoilRow | null>(
    () =>
      bulkEditMode && !isViewOnly
        ? {
            id: ADD_ROW_ID,
            interfaceId: String(interfaceId),
            description: '',
            holdingCapacity: 0,
            wiltingPoint: 0,
            intakeRate: 0,
            maxSurfaceAccumulation: 0,
          }
        : null,
    [bulkEditMode, isViewOnly, interfaceId],
  )

  // Leaving bulk edit ends the staged batch, whichever way it ended.
  useEffect(() => {
    if (bulkEditMode) return
    draftsRef.current = []
    setDrafts([])
    setInvalidRowIds(new Set())
    draftIdRef.current = DRAFT_ID_START
  }, [bulkEditMode])

  const rowData = useMemo(() => {
    const merged = rows.map((row) => {
      const edit = pendingEdits.get(String(row.id))
      return edit ? { ...row, description: edit.description } : row
    })
    // A draft only carries a name: the create endpoint takes the description
    // alone, and the numeric properties are filled in later from the drawer.
    const staged = drafts.map<SoilRow>((draft) => ({
      id: draft.id,
      interfaceId: String(interfaceId),
      description: draft.description,
      holdingCapacity: 0,
      wiltingPoint: 0,
      intakeRate: 0,
      maxSurfaceAccumulation: 0,
    }))
    const all = [...merged, ...staged]
    return addRow ? [...all, addRow] : all
  }, [rows, pendingEdits, drafts, addRow, interfaceId])

  const getRowId = useCallback((row: SoilRow) => String(row.id), [])

  // Keep the staged drafts and the "+ Add Soil" chip below the saved rows
  // regardless of sort, in the order they were added.
  const postSort = useCallback((list: SoilRow[]) => {
    const chip = list.find((row) => row.id === ADD_ROW_ID)
    const rest = list.filter((row) => row.id !== ADD_ROW_ID)
    const staged = rest
      .filter((row) => isDraftId(row.id))
      .sort((a, b) => Number(b.id) - Number(a.id))
    const merged = [...rest.filter((row) => !isDraftId(row.id)), ...staged]
    return chip ? [...merged, chip] : merged
  }, [])

  // --- Staged drafts --------------------------------------------------------

  // The row is only in the DOM after the drafts state has rendered, so the
  // editor waits a tick.
  const startEditingRow = useCallback((rowId: string) => {
    setTimeout(() => {
      gridApiRef.current?.scrollToRow(rowId)
      gridApiRef.current?.startEditingCell(rowId, 'description')
    }, 0)
  }, [])

  /**
   * `values` arrive from the quick-add chips, which stage a named row rather
   * than writing one — the chip is a shortcut for typing the name, not a
   * decision to create the record. A chip-staged row is not opened for editing:
   * it already says what the user asked for, and stealing focus would fight the
   * next chip click.
   */
  const handleAddDraft = useCallback(
    (values?: { description?: string }) => {
      if (isViewOnly) return
      gridApiRef.current?.stopEditing()
      const id = String(draftIdRef.current)
      draftIdRef.current -= 1
      const next = [
        ...draftsRef.current,
        { id, description: values?.description ?? '' },
      ]
      draftsRef.current = next
      setDrafts(next)
      noteUserEdit()
      if (!values?.description) startEditingRow(id)
      else gridApiRef.current?.scrollToRow(id)
    },
    [isViewOnly, startEditingRow, noteUserEdit],
  )

  /** Drops a single staged draft, leaving the rest of the batch alone. */
  const handleDiscardDraft = useCallback((id: string) => {
    const next = draftsRef.current.filter((draft) => draft.id !== id)
    draftsRef.current = next
    setDrafts(next)
    setInvalidRowIds((prev) => {
      if (!prev.has(id)) return prev
      const remaining = new Set(prev)
      remaining.delete(id)
      return remaining
    })
  }, [])

  const handleCellValueChanged = useCallback(
    (event: {
      row: SoilRow
      field: string
      oldValue: unknown
      newValue: unknown
    }) => {
      noteUserEdit()
      if (event.field !== 'description') return

      // Typing into a staged draft stages the name — it is not written until
      // the toolbar Save runs, exactly like an edit to an existing row. The ref
      // is written straight away as well: Save blurs the open editor, which
      // commits through here in the same tick, where state would still be stale.
      if (isDraftId(event.row.id)) {
        const typed = String(event.newValue ?? '')
        const next = draftsRef.current.map((draft) =>
          draft.id === event.row.id ? { ...draft, description: typed } : draft,
        )
        draftsRef.current = next
        setDrafts(next)
        if (typed.trim()) {
          setInvalidRowIds((prev) => {
            if (!prev.has(event.row.id)) return prev
            const remaining = new Set(prev)
            remaining.delete(event.row.id)
            return remaining
          })
        }
        return false as const
      }

      // The chip row carries the "+ Add …" affordance and holds no value.
      if (event.row.id === ADD_ROW_ID) return false as const

      const next = String(event.newValue ?? '').trim()
      const previous = String(event.oldValue ?? '')

      // Clearing the name is not a delete, and an over-long one is rejected —
      // both put the old value back.
      if (!next) return false as const
      if (next.length > CONFIG_NAME_MAX_LENGTH) {
        toast.error(
          t(`Name must not exceed ${CONFIG_NAME_MAX_LENGTH} characters`),
        )
        return false as const
      }
      if (next === previous) return false as const

      const row = event.row
      updatePendingEdits((prev) => {
        const map = new Map(prev)
        map.set(String(row.id), {
          description: next,
          holdingCapacity: row.holdingCapacity,
          wiltingPoint: row.wiltingPoint,
          intakeRate: row.intakeRate,
          maxSurfaceAccumulation: row.maxSurfaceAccumulation,
        })
        return map
      })
      return false as const
    },
    [t, noteUserEdit, updatePendingEdits],
  )

  // Enter needs no handling of its own: the text editor commits on Enter, which
  // stages the name through `handleCellValueChanged` like any other edit.

  // --- Bulk save / cancel ---------------------------------------------------

  const performSave = useCallback(async (options?: GuidedSaveOptions) => {
    // Commits the open editor before the staged rows are read. Not a
    // `sleep(0)`: a macrotask is throttled to one callback a minute in a
    // hidden tab, which parked the save (SCRUM-1032 QC, 6 Oct). From the
    // autosave with an editor still open, that row is skipped instead and
    // its editor left alone (SCRUM-1032 QC, 30 Oct).
    const heldRowId = commitOrHoldOpenEditor(gridApiRef.current, options)

    const stagedDrafts = draftsRef.current
      .filter((draft) => String(draft.id) !== heldRowId)
      .map((draft) => ({
      ...draft,
      description: draft.description.trim(),
    }))

    // Every staged row is validated before anything is written: a half-applied
    // save would leave the user guessing which rows landed.
    const unnamed = stagedDrafts.filter((draft) => !draft.description)
    const tooLong = stagedDrafts.filter(
      (draft) => draft.description.length > CONFIG_NAME_MAX_LENGTH,
    )
    const invalid = [...unnamed, ...tooLong]
    if (invalid.length > 0) {
      setInvalidRowIds(new Set(invalid.map((draft) => draft.id)))
      toast.error(
        unnamed.length > 0
          ? t('Name is required')
          : t(`Name must not exceed ${CONFIG_NAME_MAX_LENGTH} characters`),
      )
      // A non-zero `failed` keeps the page in bulk edit, so the batch survives.
      return { failed: invalid.length, total: stagedDrafts.length }
    }
    setInvalidRowIds(new Set())

    // Every edit on a row the grid has fetched is sent, including one a search
    // now hides — skipping those reported a clean save while the value never
    // reached the server. An edit on a row an unfiltered list no longer has
    // (deleted, here or elsewhere) cannot land: it is dropped rather than
    // 404ing on every pass and holding the step for good.
    const { send: edits, gone } = splitEditsForSave(
      heldRowId
        ? withoutKey(pendingEditsRef.current, heldRowId)
        : pendingEditsRef.current,
      new Set(knownRowsRef.current.keys()),
      listCompleteRef.current,
    )
    if (gone.length > 0)
      updatePendingEdits((prev) =>
        gone.reduce((map, id) => withoutKey(map, id), prev),
      )

    // Thunks, not promises: firing every request up front sent the whole batch
    // in parallel, and this API returns 500 under concurrent writes.
    const updateTasks = edits.map(
      ([id, edit]) =>
        () =>
          soilDataConfigApi.update({
            id: Number(id),
            interfaceId: Number(interfaceId),
            description: edit.description,
            // Only send numeric fields when non-zero: the API validates
            // wiltingPoint < holdingCapacity and rejects a pair of zeroes.
            ...(edit.holdingCapacity
              ? { holdingCapacity: edit.holdingCapacity }
              : {}),
            ...(edit.wiltingPoint ? { wiltingPoint: edit.wiltingPoint } : {}),
            ...(edit.intakeRate ? { intakeRate: edit.intakeRate } : {}),
            ...(edit.maxSurfaceAccumulation
              ? { maxSurfaceAccumulation: edit.maxSurfaceAccumulation }
              : {}),
          }),
    )
    // The create takes the name only — the same reason the update omits zeroes,
    // a new soil has no numeric properties to validate yet.
    const createTasks = stagedDrafts.map(
      (draft) => () =>
        soilDataConfigApi.create({
          interfaceId,
          description: draft.description,
        }),
    )

    const tasks = [...updateTasks, ...createTasks]
    if (tasks.length === 0) return { failed: 0, total: 0 }

    // One at a time, with a retry: the API returns 500 when writes arrive
    // concurrently, which silently dropped most of a large batch.
    let lastError: any = null
    const failedTaskIndexes = new Set<number>()

    for (const [index, task] of tasks.entries()) {
      let saved = false

      for (let attempt = 0; attempt < SAVE_RETRY_LIMIT && !saved; attempt++) {
        try {
          await task()
          saved = true
        } catch (error: any) {
          lastError = error
          // A 4xx is the server refusing the value: resending cannot help.
          if (!isRetryableApiError(error)) break
          if (attempt < SAVE_RETRY_LIMIT - 1) await sleep(SAVE_RETRY_DELAY_MS)
        }
      }

      if (!saved) failedTaskIndexes.add(index)
      await sleep(SAVE_REQUEST_GAP_MS)
    }

    // Retire what landed from the CURRENT staged state, not from the copy
    // taken when the save began: a chip clicked or a name typed while the
    // requests were out is newer than that copy and must survive. Failed
    // edits and drafts stay staged as they are (SCRUM-1032 review, round 7).
    const landedEdits = edits.filter((_, index) => !failedTaskIndexes.has(index))
    updatePendingEdits((prev) => retireSavedRecords(prev, landedEdits))

    // The creates trail the updates in `tasks`, so draft N is task N + offset.
    // Re-staging a draft that landed would create it a second time.
    const createOffset = updateTasks.length
    const landedDraftIds = stagedDrafts
      .filter((_, index) => !failedTaskIndexes.has(index + createOffset))
      .map((draft) => draft.id)
    draftsRef.current = retireCreatedDrafts(draftsRef.current, landedDraftIds)
    setDrafts(draftsRef.current)

    const failed = failedTaskIndexes.size

    if (failed === 0) {
      // SCRUM-1032: inside the wizard the step shows one status for every
      // grid on it, so six chips added one at a time no longer raise six
      // toasts. On /config, Save is a deliberate act and still confirms.
      if (!isGuidedSetup)
        toast.success(
          createTasks.length > 0 && updateTasks.length === 0
            ? t('addSoilSuccessfully')
            : t('updateSoilSuccessfully'),
        )
    } else if (failed === tasks.length) {
      // The server's reason, under one id so a repeat replaces it.
      toast.error(getApiErrorMessage(lastError, t('somethingWentWrong')), {
        id: 'soil-save-error',
      })
    } else {
      toast.error(t('bulkSavePartialError', { failed, total: tasks.length }), {
        id: 'soil-save-error',
      })
    }

    if (failed < tasks.length) refresh()

    return { failed, total: tasks.length }
  }, [interfaceId, t, refresh, updatePendingEdits, isGuidedSetup])

  // SCRUM-1032 QC items 12 / 15b — one save at a time. The pause autosave
  // and the wizard's Next flush both call this, and two overlapping runs
  // read the same staged rows and write them twice.
  const { save: handleSave, isSaving } = useSerialisedSave(performSave)

  /** A deleted row takes its staged edit with it; nothing is left "held". */
  const forgetRow = useCallback(
    (id: string) => {
      knownRowsRef.current.delete(String(id))
      updatePendingEdits((prev) => withoutKey(prev, String(id)))
    },
    [updatePendingEdits],
  )

  const handleCancel = useCallback(() => {
    updatePendingEdits(() => new Map())
    draftsRef.current = []
    setDrafts([])
    setInvalidRowIds(new Set())
    draftIdRef.current = DRAFT_ID_START
    refresh()
  }, [refresh, updatePendingEdits])

  // Expose the toolbar actions to the parent without global window handles.
  useEffect(() => {
    actionsRef.current = {
      save: handleSave,
      cancel: handleCancel,
      refresh,
      hasPendingEdits: () =>
        pendingEditsRef.current.size > 0 || draftsRef.current.length > 0,
      forgetRow,
      addDraft: handleAddDraft,
    }
    return () => {
      actionsRef.current = null
    }
  }, [actionsRef, handleSave, handleCancel, refresh, forgetRow, handleAddDraft])

  // A row still missing its name cannot be written — `handleSave` rejects it
  // and marks the cell — so autosave waits rather than raising the same
  // complaint every few seconds while the user is still typing it. A chip
  // stages a named row, so only a hand-added blank one holds the batch.
  const draftsReadyToSave = drafts.every((draft) =>
    String(draft.description ?? '').trim(),
  )

  useGuidedAutosave({
    enabled: isGuidedSetup && bulkEditMode && !isViewOnly && draftsReadyToSave,
    hasPendingChanges: pendingEdits.size > 0 || drafts.length > 0,
    // User edits only: see useUserEditVersion.
    version: userEditVersion,
    save: handleSave,
    keepsEditorOpen: true,
    // An open editor is the user mid-value; the save would close it.
    isEditing: () => gridApiRef.current?.isEditingCell() ?? false,
    isBusy: isSaving,
  })

  // The chips show a name as added while it is only staged, so one cannot be
  // staged twice before the autosave writes it.
  useEffect(() => {
    onStagedNamesChange?.(drafts.map((draft) => draft.description))
  }, [drafts, onStagedNamesChange])

  const ActionsCellRenderer = useCallback(
    ({ row }: GridCellCtx<SoilRow>) => {
      if (!row) return null

      // The chip row is an affordance, not a record — nothing to act on.
      if (row.id === ADD_ROW_ID) return null
      if (!bulkEditMode) return null

      // A draft gets the same ✕ as a saved row, so one staged item can be
      // dropped without cancelling the whole batch. It has never been written,
      // so it is discarded locally instead of going through the delete modal.
      const isDraft = isDraftId(row.id)

      return (
        <Box
          data-no-cell-edit
          sx={{
            display: 'flex',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DeleteIconButton
            disabled={isViewOnly}
            isDark={isDark}
            onClick={() => {
              if (isViewOnly) return
              if (isDraft) handleDiscardDraft(row.id)
              else
                onDeleteItem?.(
                  row.id,
                  String(row.interfaceId ?? interfaceId),
                  row.description,
                )
            }}
          />
        </Box>
      )
    },
    [
      bulkEditMode,
      isViewOnly,
      isDark,
      onDeleteItem,
      interfaceId,
      handleDiscardDraft,
    ],
  )

  const columns = useMemo<GridColumn<SoilRow>[]>(
    () => [
      {
        field: 'description',
        headerName: t('menuDataConfigSoil'),
        flex: 1,
        // These panels sit four-across and can be ~218px wide; a 200px floor
        // here left no room for the action column, so the row overflowed and
        // scrolled instead of fitting.
        minWidth: 120,
        editable: ({ row }) =>
          row.id === ADD_ROW_ID ? false : bulkEditMode && !isViewOnly,
        editor: 'text',
        editorParams: { maxLength: CONFIG_NAME_MAX_LENGTH },
        cellRenderer: ({ row, value }) => {
          if (row.id === ADD_ROW_ID) {
            return (
              <Box
                component="span"
                data-no-cell-edit
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '2px 10px',
                  height: '40px',
                  borderRadius: '4px',
                  border: isViewOnly
                    ? '1px dashed #e0e0e0'
                    : `1px dashed ${
                        isDark
                          ? 'rgba(52, 211, 153, 0.5)'
                          : 'rgba(0, 71, 68, 0.5)'
                      }`,
                  cursor: isViewOnly ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  color: isViewOnly
                    ? '#bdbdbd'
                    : isDark
                      ? '#5fe0ad'
                      : '#004744',
                }}
                onClick={() => handleAddDraft()}
              >
                + {t('actionAddSoil')}
              </Box>
            )
          }
          return <>{value as string}</>
        },
        cellClass: ({ row }) => {
          const list: string[] = []
          // A draft takes the whole-row `new` tint instead of the per-cell
          // editable ring, so the two signals stay distinguishable.
          if (
            bulkEditMode &&
            !isViewOnly &&
            row.id !== ADD_ROW_ID &&
            !isDraftId(row.id)
          ) {
            list.push(gridCellClasses.editable)
          }
          if (pendingEdits.has(String(row.id))) {
            list.push(gridCellClasses.changed)
          }
          if (invalidRowIds.has(row.id)) list.push(gridCellClasses.invalid)
          return list
        },
      },
      // Delete is the only action and it only applies in bulk edit. Outside
      // it the renderer drew nothing but the column still reserved its width,
      // which was enough to push these narrow panels into a horizontal
      // scrollbar over an empty strip.
      ...(bulkEditMode
        ? [
            {
              field: 'actions',
              headerName: '',
              // One 30px icon button. 84 left a band of empty space beside it
              // and, with the name column's minimum, pushed the row wider than
              // the panel.
              width: 52,
              // Pinned so Delete stays reachable: these panels are narrow and
              // the row scrolls internally, which used to carry the action
              // column out of sight. Matches EtReference.
              pinned: 'right' as const,
              sortable: false,
              resizable: false,
              editable: false,
              cellRenderer: ActionsCellRenderer,
              cellStyle: {
                justifyContent: 'flex-end',
                paddingRight: '12px',
                overflow: 'visible',
              },
            },
          ]
        : []),
    ],
    [
      t,
      ActionsCellRenderer,
      bulkEditMode,
      isViewOnly,
      isDark,
      pendingEdits,
      invalidRowIds,
      handleAddDraft,
    ],
  )

  const rowClass = useCallback(
    ({ row }: { row: SoilRow }) =>
      isDraftId(row.id) ? gridRowClasses.new : undefined,
    [],
  )

  return (
    <DataGrid<SoilRow>
      columns={columns}
      rows={rowData}
      loading={!loadedOnce}
      getRowId={getRowId}
      postSort={postSort}
      apiRef={gridApiRef}
      rowHeight={40}
      headerHeight={42}
      editMode="singleClick"
      rowClass={rowClass}
      onCellValueChanged={handleCellValueChanged}
    />
  )
}

export default SoilTable
